// Manufactured Home Templates editor.
//
// Templates are name + fixed price only. They pin a job's customer
// total to a preset value at create time; the office fills line items
// via the PricingCalculator (or the crew submits an estimate via the
// bot) — same path as any non-template job.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../lib/queryKeys.js';
import { Home, Pencil, Check, X, Trash2, Plus } from 'lucide-react';
import {
  getJobTemplates,
  patchJobTemplate,
  deleteJobTemplate,
  createJobTemplate,
} from '../api/client.js';
import type { JobTemplate } from '@gutterinv/shared';
import { Card, CardContent } from './ui/card.js';
import { Badge } from './ui/badge.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Skeleton } from './ui/skeleton.js';
import { formatCents } from '../lib/format.js';

export function TemplatesSection() {
  const { data: templates, isLoading, error } = useQuery({
    queryKey: qk.jobs.templates(),
    queryFn: getJobTemplates,
  });
  const [creating, setCreating] = useState(false);

  const sortedTemplates = [...(templates ?? [])].sort(
    (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
  );

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="eyebrow flex items-center gap-1.5">
          <Home className="h-3 w-3" />
          Manufactured Home Templates
        </h2>
        {!creating && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setCreating(true)}
          >
            <Plus className="h-3 w-3" />
            New template
          </Button>
        )}
      </div>
      <p className="text-xs text-taupe">
        Named price presets for standard manufactured-home jobs. Pick one
        on the New Job dialog to pin the customer total. Line items still
        come from the crew estimate or the office's pricing calculator.
      </p>

      {isLoading ? (
        <Skeleton className="h-40" />
      ) : error ? (
        <Card className="border-destructive p-6">
          <div className="font-semibold text-destructive">
            Failed to load templates
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : String(error)}
          </div>
        </Card>
      ) : (
        <div className="grid gap-3">
          {creating && (
            <NewTemplateCard onDone={() => setCreating(false)} />
          )}
          {sortedTemplates.map((t) => (
            <TemplateCard key={t.id} template={t} />
          ))}
        </div>
      )}
    </section>
  );
}

// ============================================================
// NEW TEMPLATE CARD (inline form)
// ============================================================

function NewTemplateCard({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [priceDollars, setPriceDollars] = useState('');

  const create = useMutation({
    mutationFn: () => {
      const price = Math.round(Number(priceDollars) * 100);
      return createJobTemplate({
        name: name.trim(),
        fixedPriceCents: Number.isFinite(price) && price >= 0 ? price : 0,
        active: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.jobs.templates() });
      onDone();
    },
  });

  const canSubmit = name.trim().length > 0 && !create.isPending;

  return (
    <Card className="border-dashed">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <label className="eyebrow flex-shrink-0 w-10">Name</label>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. 80-90 ft Manufactured Home + Gutter Guards"
            className="h-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="eyebrow flex-shrink-0 w-10">Price</label>
          <span className="text-xs text-taupe">$</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={priceDollars}
            onChange={(e) => setPriceDollars(e.target.value)}
            placeholder="0.00"
            className="h-8 text-sm"
          />
        </div>
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            className="h-7 flex-1 text-xs"
            disabled={!canSubmit}
            onClick={() => create.mutate()}
          >
            <Check className="h-3 w-3" />
            Create
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={create.isPending}
            onClick={onDone}
          >
            <X className="h-3 w-3" />
            Cancel
          </Button>
        </div>
        {create.error && (
          <div className="text-xs text-destructive">
            {create.error instanceof Error
              ? create.error.message
              : 'Create failed'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// ONE TEMPLATE CARD — name, price, active, delete
// ============================================================

function TemplateCard({ template }: { template: JobTemplate }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [name, setName] = useState(template.name);
  const [priceDollars, setPriceDollars] = useState(
    (template.fixedPriceCents / 100).toFixed(2),
  );
  const [active, setActive] = useState(template.active);

  const patchMeta = useMutation({
    mutationFn: () => {
      const price = Math.round(Number(priceDollars) * 100);
      return patchJobTemplate(template.id, {
        name: name.trim() || template.name,
        fixedPriceCents: Number.isFinite(price) && price >= 0 ? price : undefined,
        active,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.jobs.templates() });
      setEditing(false);
    },
  });

  const removeTemplate = useMutation({
    mutationFn: () => deleteJobTemplate(template.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.jobs.templates() });
    },
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {editing ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="eyebrow flex-shrink-0 w-10">Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="eyebrow flex-shrink-0 w-10">Price</label>
              <span className="text-xs text-taupe">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={priceDollars}
                onChange={(e) => setPriceDollars(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="eyebrow flex-shrink-0 w-10">Active</label>
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                className="h-7 flex-1 text-xs"
                disabled={patchMeta.isPending}
                onClick={() => patchMeta.mutate()}
              >
                <Check className="h-3 w-3" />
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                disabled={patchMeta.isPending}
                onClick={() => {
                  setName(template.name);
                  setPriceDollars((template.fixedPriceCents / 100).toFixed(2));
                  setActive(template.active);
                  setEditing(false);
                }}
              >
                <X className="h-3 w-3" />
                Cancel
              </Button>
            </div>
            {patchMeta.error && (
              <div className="text-xs text-destructive">
                {patchMeta.error instanceof Error
                  ? patchMeta.error.message
                  : 'Save failed'}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-foreground">
                  {template.name}
                </h3>
                {!template.active && (
                  <Badge variant="secondary" className="text-[0.625rem]">
                    Inactive
                  </Badge>
                )}
              </div>
              <div className="mt-0.5 text-sm text-muted-foreground">
                {formatCents(template.fixedPriceCents)}
                {template.fixedPriceCents === 0 && (
                  <span className="ml-2 text-[0.625rem] text-destructive">
                    price not set
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-3 w-3" />
                Edit
              </Button>
              {confirmingDelete ? (
                <>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={removeTemplate.isPending}
                    onClick={() => removeTemplate.mutate()}
                  >
                    <Check className="h-3 w-3" />
                    Confirm
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={removeTemplate.isPending}
                    onClick={() => setConfirmingDelete(false)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => setConfirmingDelete(true)}
                  aria-label="Delete template"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}
        {removeTemplate.error && (
          <div className="text-xs text-destructive">
            {removeTemplate.error instanceof Error
              ? removeTemplate.error.message
              : 'Delete failed'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
